// Banners that surface missing API keys.
// - <MissingApiKeyBanner /> — global, mounted near the app header. Required
//   key (OpenAI) only. Optional providers (ElevenLabs, OpenAI Admin) are not
//   surfaced globally — they get inline notices on the pages that need them.
// - <ProviderKeyMissing provider="elevenlabs" feature="Podcast" /> — inline
//   notice for any feature blocked by a missing optional key. Shows what the
//   feature is, why it's disabled, and a deep link to Settings.

import React, { useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { preferencesAPI } from '../services/api';
import { queryKeys } from '../queryKeys';

const PROVIDER_LABELS = {
  openai_api: 'OpenAI',
  openai_admin: 'OpenAI Admin',
  elevenlabs: 'ElevenLabs',
};
const PROVIDER_PREF_KEY = {
  openai_api: 'active_openai_api_key_id',
  openai_admin: 'active_openai_admin_key_id',
  elevenlabs: 'active_elevenlabs_key_id',
};

function usePrefsKeySet(provider) {
  const { data: prefs, isError, isLoading } = useQuery({
    queryKey: queryKeys.preferences.all,
    queryFn: () => preferencesAPI.getPreferences(),
    retry: false,
    staleTime: 60_000,
  });
  const isSet = !!prefs?.[PROVIDER_PREF_KEY[provider]];
  return { isSet, isError, isLoading };
}

// Fallback spacer height before the bar has been measured (one text line).
const BANNER_FALLBACK_HEIGHT = 36;

export default function MissingApiKeyBanner() {
  const { isSet, isError, isLoading } = usePrefsKeySet('openai_api');
  const visible = !(isError || isLoading || isSet);
  const barRef = useRef(null);
  const [height, setHeight] = useState(BANNER_FALLBACK_HEIGHT);

  // The bar is fixed, so it takes no layout space; the spacer below must
  // match its real height or the bar covers the page top whenever the text
  // wraps (a fixed h-9 hid ~140px of content on phones).
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!visible || !el) return undefined;
    const measure = () => setHeight(Math.ceil(el.getBoundingClientRect().height) || BANNER_FALLBACK_HEIGHT);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {/* Fixed to the top edge (there is no top nav anymore); on the Studio
          shell the CSS var shifts it right so it clears the rail. Spacer
          reserves layout space below the bar so page content isn't covered. */}
      <div
        ref={barRef}
        className="missing-api-key-banner fixed inset-x-0 z-40 bg-[var(--warn-bg)] border-b border-[var(--warn-accent)]/40 text-[var(--warn)] px-4 py-2 text-sm flex items-center gap-3"
        style={{ top: 'var(--app-nav-height, 0px)' }}
      >
        <span aria-hidden>⚠</span>
        <span className="flex-1 min-w-0">
          <strong>Kein OpenAI-Schlüssel konfiguriert.</strong>
          <span className="hidden md:inline">
            {' '}Wortklassifikation, Fragen und Validierung sind deaktiviert, bis du einen Schlüssel hinzufügst.
          </span>
        </span>
        <a
          href="/settings?tab=api_keys"
          className="inline-flex items-center min-h-[44px] md:min-h-0 px-3 py-1 bg-[var(--warn)] text-white text-xs font-medium hover:bg-[var(--ink-900)] whitespace-nowrap"
        >
          <span className="md:hidden">Einrichten →</span>
          <span className="hidden md:inline">Zu den API-Schlüsseln →</span>
        </a>
      </div>
      <div aria-hidden data-testid="missing-api-key-spacer" style={{ height }} />
    </>
  );
}

/**
 * Inline notice for an *optional* provider that's missing.
 * Use on any page whose primary action depends on the provider.
 *
 * Props:
 *   provider — "elevenlabs" | "openai_admin"
 *   feature  — short German noun-phrase, e.g. "Podcast-Erzeugung", "Live-Kostenanzeige"
 *   className — optional extra classes
 */
export function ProviderKeyMissing({ provider, feature, className = '' }) {
  const { isSet, isError, isLoading } = usePrefsKeySet(provider);
  if (isError || isLoading || isSet) return null;

  return (
    <div className={`bg-[var(--warn-accent)]/10 border border-[var(--warn-accent)]/40 text-[var(--warn)] p-3 text-sm flex items-center gap-3 ${className}`}>
      <span>⚠</span>
      <span className="flex-1">
        <strong>{feature}</strong> ist deaktiviert, weil kein {PROVIDER_LABELS[provider]}-Schlüssel konfiguriert ist.
      </span>
      <a
        href="/settings?tab=api_keys"
        className="px-3 py-1 bg-[var(--warn)] text-white text-xs font-medium hover:bg-[var(--ink-900)] whitespace-nowrap"
      >
        Schlüssel hinzufügen →
      </a>
    </div>
  );
}
